import { useEffect, useRef } from 'react';
import { useSWRConfig } from 'swr';
import { useHeaderInfo } from '@hooks/features/useHeaderInfo';


/**
 * @description Clears the whole SWR cache when the **identity** behind it changes — i.e. the cached
 * data belongs to a different user than the one now signed in.
 *
 * Previously named `useRevalidateOnRefocus`, which described neither the trigger (a user switch, not
 * a tab refocus — it no longer listens to `visibilitychange` at all) nor the action (clearing the
 * cache, not revalidating it).
 *
 * History, because the trigger has drifted once already:
 * - `89e0fa6e4` (2023) introduced this with `revalidateAuthUser()`: it required the user to be
 *   signed in AND the auth cookie's date to differ from the cached one — "revalidate if the user is
 *   different". Precise, and it never fired for anonymous visitors.
 * - `1f2478864` (2025, app-router auth cleanup) replaced that with `status === 'unauthenticated'`
 *   from `useSession()`. But that is the *permanent* state of every anonymous visitor, so the whole
 *   cache was being blanked on every single tab refocus for logged-out traffic — job lists, SEO /
 *   breadcrumb data, static lists — forcing a visible refetch storm each time. The docstring said
 *   "user logged out"; the condition actually read "user is not logged in".
 *
 * This restores the original intent. `revalidateAuthUser` itself cannot come back: it read the
 * `rm_pbl` cookie from JS, and the NextAuth session cookie (`_ast`) is httpOnly, so identity is no
 * longer visible client-side. Instead we watch `userId` on the header-info Redux slice, which the
 * Header already refreshes on tab refocus (`useHeaderInfoData` → `useOnTabRefocus`) — so no extra
 * request is needed, and an identity change is caught whether or not it coincides with a refocus.
 *
 * Deliberately narrow: only a switch between two *known* users clears the cache.
 * - anonymous → anonymous: never fires (the regression this fixes)
 * - initial hydration (nothing → user): the cache already belongs to that user, nothing to clear
 * - logout: handled by `useHeaderInfoData`'s 401 path, which calls `signOut()` and hard-navigates,
 *   discarding every cache anyway. Treating "header info disappeared" as logout would misfire,
 *   because its `onError` clears the Redux slice on *any* failure, not only a 401.
 */
export const useClearCacheOnUserChange = () => {
  const { mutate: globalMutate } = useSWRConfig();
  const headerInfo = useHeaderInfo();
  const currentUserId = headerInfo?.userId ?? null;

  // Identity the current cache was populated for; `undefined` until first observed.
  const cachedUserIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    const previousUserId = cachedUserIdRef.current;
    cachedUserIdRef.current = currentUserId;

    // Nothing known on one side of the transition — hydration, logout, or a transient
    // header-info error. None of those is a user switch.
    if (previousUserId === undefined || previousUserId === null || currentUserId === null) {
      return;
    }

    if (previousUserId === currentUserId) {
      return;
    }

    // Different user: drop everything cached for the previous one.
    void globalMutate(
      () => true, // which cache keys are updated => all of them
      undefined, // update cache data to `undefined`
    );
  }, [currentUserId, globalMutate]);
};
